import React from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { DatePicker } from "@mantine/dates";
import { format, startOfDay, isToday } from "date-fns";
import { cn } from "@/lib/utils";
import { nowInLocal } from "@/utils/timezone";

export interface UnifiedDatePickerProps {
  date?: Date;
  onChange?: (date?: Date) => void;
  placeholder?: string;
  buttonClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: (date: Date) => boolean;
  align?: "start" | "center" | "end";
  variant?: "simple" | "enhanced";
}

export function UnifiedDatePicker({
  date,
  onChange,
  placeholder = "Pick a date",
  buttonClassName,
  minDate,
  maxDate,
  disabled,
  align = "start",
  variant = "enhanced",
}: UnifiedDatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  
  const isDisabled = (d: Date) => {
    const day = startOfDay(d);
    if (minDate && day < startOfDay(minDate)) return true;
    if (maxDate && day > startOfDay(maxDate)) return true;
    return disabled ? disabled(d) : false;
  };

  const handleTodayClick = () => {
    const today = nowInLocal();
    if (!isDisabled(today)) {
      onChange?.(today);
      setIsOpen(false);
    }
  };

  const handleClearClick = () => {
    onChange?.(undefined);
    setIsOpen(false);
  };

  const canSelectToday = () => {
    const today = nowInLocal();
    return !isDisabled(today);
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (open) console.debug('[UnifiedDatePicker]', { variant, date }); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal gap-2",
            !date && "text-muted-foreground",
            buttonClassName
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
          {date ? (
            <span className="truncate">{format(date, "PPP")}</span>
          ) : (
            <span>{placeholder}</span>
          )}
          {date && (
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-auto hover:bg-muted-foreground/20"
              onClick={(e) => {
                e.stopPropagation();
                handleClearClick();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-80 p-0" align={align}>
        {variant === "simple" ? (
          <div className="p-4 flex justify-center pointer-events-auto" data-testid="udp-simple-mantine">
            <DatePicker
              value={date ?? null}
              onChange={(d) => {
                const dateValue = d ? new Date(d) : undefined;
                onChange?.(dateValue);
                if (d) setIsOpen(false);
              }}
              defaultDate={date ?? nowInLocal()}
              minDate={minDate}
              maxDate={maxDate}
              excludeDate={disabled ? (dateStr: string) => disabled(new Date(dateStr)) : undefined}
              firstDayOfWeek={1}
              allowDeselect={false}
              size="sm"
            />
          </div>
        ) : (
          <div className="flex flex-col" data-testid="udp-enhanced-daypicker">
            <DayPicker
              mode="single"
              selected={date}
              onSelect={(d) => {
                onChange?.(d);
                if (d) setIsOpen(false);
              }}
              disabled={isDisabled}
              defaultMonth={date ?? nowInLocal()}
              className={cn("px-6 py-4 pointer-events-auto")}
              initialFocus
            />
            <div className="flex items-center justify-between gap-3 p-4 border-t bg-muted/30">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTodayClick}
                disabled={!canSelectToday()}
                className="flex-1"
              >
                Today
              </Button>
              {date && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearClick}
                  className="flex-1"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default UnifiedDatePicker;
